import { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import Seo from "@/components/Seo";
import { images, pageImages } from "@/data";

const aboutStats = [
  { value: "98%", title: "Client Retention Rate", desc: "People with us year after year, because trust isn\u2019t built in one trip, it\u2019s built in every one after." },
  { value: "4.8\u2605", title: "Average Traveler Rating", desc: "Thousands of post-trip reviews reflect our obsessive focus on detail, comfort, and care." },
  { value: "30+ Years", title: "In Successful Operations", desc: "We\u2019ve weathered industry shifts, digital disruptions, and border shutdowns, and we\u2019ve grown stronger every time." },
];

const whyCards = [
  { img: pageImages.aboutWhy1, title: "Local Insight, Global Scale", desc: "On-ground teams in 15+ countries and 500+ vendor partnerships give us unmatched control over quality and experience." },
  { img: pageImages.aboutWhy2, title: "Top 1% Vendor Network", desc: "From airlines to hotels to local specialists, we\u2019ve spent years building connections that unlock exclusive access and better pricing." },
  { img: pageImages.aboutWhy3, title: "Thousands Served", desc: "100,000+ travelers across B2B, corporate, and influencer segments trust us for seamless delivery, no matter the brief." },
  { img: pageImages.aboutWhy4, title: "Sustainable Practices", desc: "From partnering with eco-conscious hotels and low-emission transport providers, our aim is to create journeys that give back more than they take." },
];

const timeline = [
  { year: "1995", title: "Welcoming the World to India", img: pageImages.hist1995, desc: "Our story started in 1995 as a boutique inbound travel company, curating authentic, high-touch experiences for international travellers discovering India. From heritage tours to immersive local journeys, we built our reputation one guest at a time." },
  { year: "2005", title: "Hi DMC Takes Flight", img: pageImages.hist2005, desc: "As demand grew, we evolved into a full-service destination management company expanding across the Indian Subcontinent and setting a new benchmark in quality, logistics, and cultural storytelling for global travel partners." },
  { year: "2015", title: "Expanding Into New Travel Verticals", img: pageImages.hist2015, desc: "With a strong foundation in B2B travel, we branched into domestic holidays, curated offsites for Indian companies, and niche segments like wellness, adventure, and cultural circuits \u2014 building specialized offerings for every kind of traveller." },
  { year: "2025", title: "30 years strong, The Hi Tours Group is Born", img: pageImages.hist2025, desc: "In 2025, we unified our growing portfolio under the Hi Tours Group umbrella with distinct verticals like Hi DMC (global B2B travel), Hi Offsite (corporate experiences), and Hi Tours (leisure travel). One group, many journeys." },
];

const AboutPage = () => {
  const [active, setActive] = useState(3);
  const t = timeline[active];
  return (
    <main className="page">
      <Seo
        title="About Us"
        path="/about"
        description="30+ years elevating the way the world travels. Meet the operators, strategists, and local experts behind HI Tours Group and Hi DMC, Hi Tours, and Hi Offsite."
      />
      <PageHero eyebrow="About us" title="Elevating the Way the World Travels" subtitle="We’re not just building a travel group. We’re shaping a smarter, more connected way to explore the world." />

      <section className="section">
        <div className="container">
          <div className="stats__grid">
            {aboutStats.map((s) => (
              <div className="stats__item" key={s.title}>
                <img className="stats__icon" src={images.leafIcon} alt="" aria-hidden="true" />
                <div className="stats__value stats__value--sm">{s.value}</div>
                <h3 className="stats__title">{s.title}</h3>
                <p className="stats__desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section who">
        <div className="container who__inner">
          <span className="badge"><span className="badge__dot" />Who We Are</span>
          <h2 className="who__title">We’re a group of operators, strategists, technologists, creators, and local experts powering seamless execution across borders.</h2>
          <Link to="/partner" className="btn btn--solid"><span className="btn__dot" />Let’s work together</Link>
        </div>
      </section>

      <section className="section why">
        <div className="container">
          <div className="why__head why__head--left">
            <span className="badge"><span className="badge__dot" />Why choose us</span>
            <h2 className="why__title">We’re more than just a travel group</h2>
            <p className="section__lead">From logistics to experiences, we embed purpose into every journey. That means supporting local communities, reducing travel footprints, and designing trips that are as meaningful as they are memorable.</p>
          </div>
          <div className="feature-grid">
            {whyCards.map((c) => (
              <article className="feature-card" key={c.title}>
                <img className="feature-card__img" src={c.img} alt={c.title} loading="lazy" />
                <h3 className="feature-card__title">{c.title}</h3>
                <p className="feature-card__desc">{c.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section history">
        <div className="container">
          <div className="why__head">
            <span className="badge"><span className="badge__dot" />Our history</span>
            <h2 className="why__title">From humble roots to a global vision</h2>
          </div>
          <div className="history__tabs">
            {timeline.map((item, i) => (
              <button key={item.year} className={`history__tab ${i === active ? "history__tab--active" : ""}`} onClick={() => setActive(i)}>{item.year}</button>
            ))}
          </div>
          <div className="history__panel">
            <div className="history__content">
              <h3 className="history__year-title">{t.title}</h3>
              <p className="history__desc">{t.desc}</p>
            </div>
            <div className="history__image">
              <img src={t.img} alt={t.title} loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </main>
  );
};

export default AboutPage;
